import { useState } from 'react';
import { Box, Card, FlexBox, H2, Li, Link, Typography, Ul, getTokenCssVar } from '@vp/swan';
import { LayeredAvatars } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { Price } from '@99designs/pricing';
import { NormalisedBriefProduct } from '../BriefContext';
import { ReportedIssues } from '../ProductCard/ReportedIssues';
import StartingProjectDisplay from '../StartingProjectDisplay/StartingProjectDisplay';
import { BriefWorkEntityField } from './FormFields/WorkEntityField';
import { usePreferredDesignersListQuery } from './PreferredDesignersList.generated';
import { PreferredDesignersModal } from './PreferredDesignersModal';
import { Field_WorkEntityField_Fragment } from './brief.generated';
import { PREFERRED_DESIGNERS_PAGE_SIZE } from './preferredDesigners';
import type { InvitedDesigner } from './InvitedDesigner';

export interface WorkEntityDisplay {
    title: string;
    description: string | null;
    field: Field_WorkEntityField_Fragment;
}

export interface LogomakerAsset {
    id: string | null | undefined;
    sig: string | null | undefined;
}

export interface StepProductCardProps {
    product: NormalisedBriefProduct;
    startingAsset?: WorkEntityDisplay | LogomakerAsset;
    reportedIssues?: readonly string[] | null;
    invitedDesigner?: InvitedDesigner | null;
    isPricingLoading?: boolean;
}

export function StepProductCard({
    product,
    startingAsset,
    reportedIssues,
    invitedDesigner,
    isPricingLoading,
}: StepProductCardProps) {
    const isStartingAssetWorkEntity = startingAsset && 'field' in startingAsset;
    const isStartingAssetLogomker = startingAsset && 'id' in startingAsset;

    function StartingAssetDisplay() {
        if (isStartingAssetWorkEntity) {
            return (
                <Box mt={4}>
                    <BriefWorkEntityField
                        workEntityValue={startingAsset.field.workEntityValue}
                        title={startingAsset.title}
                        description={startingAsset.description}
                    />
                </Box>
            );
        }

        if (isStartingAssetLogomker) {
            return (
                <Box mt={4}>
                    <StartingProjectDisplay
                        images={[
                            {
                                url: `https://api.sherbert.cimpress.io/v2/assets/${startingAsset.id}?signature=${startingAsset.sig}`,
                            },
                        ]}
                        title={__('Your Starting Project', {
                            _context:
                                'Title for Logomaker starting project Image, which is used as a reference for designers',
                        })}
                        description={__(
                            'Your designer will use your Logomaker project as a starting point.',
                            {
                                _context:
                                    'Description for Logomaker starting project image, which is used as a reference for designers',
                            }
                        )}
                    />
                </Box>
            );
        }

        return null;
    }

    return (
        <Card
            bordered
            p="5"
            data-testid="brief-product-card"
            style={{
                minHeight: '96px',
                borderRadius: getTokenCssVar('SwanSemBorderRadiusStandard'),
                width: '100%',
            }}
        >
            <FlexBox alignItems="center" justifyContent="space-between" flexWrap="wrap-reverse">
                <FlexBox
                    justifyContent="space-between"
                    style={{ width: '100%' }}
                    data-testid="brief-product-card-summary"
                >
                    <Box data-testid="brief-product-card-content">
                        <H2
                            fontSize="large"
                            fontWeight="bold"
                            textAlign="left"
                            pr={8}
                            mb={0}
                            data-testid="brief-product-card-title"
                        >
                            {product.title}
                        </H2>
                        <Ul mt={4} data-testid="brief-product-card-inclusions">
                            {product.details.map((item, index) => (
                                <Li
                                    key={item.title}
                                    data-testid={`brief-product-card-inclusion-${index + 1}`}
                                >
                                    {item.title}
                                </Li>
                            ))}
                        </Ul>
                    </Box>
                    <Box
                        textAlign="right"
                        style={{ flexShrink: 0, minWidth: '70px' }}
                        data-testid="brief-product-card-price"
                    >
                        <Price
                            price={{
                                currency: product.pricing.currency,
                                listPrice: {
                                    taxed: product.pricing.taxed,
                                    untaxed: product.pricing.untaxed,
                                },
                            }}
                            loading={isPricingLoading}
                            skeletonWidth="standard"
                            fontSize="large"
                            fontWeight="bold"
                            subtleTaxMessage
                            stacked
                        />
                    </Box>
                </FlexBox>
            </FlexBox>
            {invitedDesigner && <PreferredDesignerBanner />}
            <StartingAssetDisplay />
            <ReportedIssues reportedIssues={reportedIssues} />
        </Card>
    );
}

function PreferredDesignerBanner() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { data } = usePreferredDesignersListQuery({
        variables: { page: 1, pageSize: PREFERRED_DESIGNERS_PAGE_SIZE },
        errorPolicy: 'all',
    });

    const avatarUrls = data?.myDesigners.designers.map((d) => d.user?.profile.avatarUrl) ?? [];

    return (
        <>
            <Box
                mt={4}
                p={4}
                style={{
                    backgroundColor: getTokenCssVar('SwanSemColorBgStrong'),
                    borderRadius: getTokenCssVar('SwanSemBorderRadiusStandard'),
                }}
            >
                <Typography fontSkin="body-standard-bold" mb={2}>
                    {__('Preferred designer')}
                </Typography>
                <FlexBox alignItems="center" gap="between-actions">
                    <LayeredAvatars avatarUrls={avatarUrls} size={28} max={3} />
                    <Link
                        skin="standard"
                        render={({ children, ...props }) => (
                            <button type="button" {...props} onClick={() => setIsModalOpen(true)}>
                                {children}
                            </button>
                        )}
                    >
                        {__("Match me with a designer I've worked with before")}
                    </Link>
                </FlexBox>
                <Typography fontSkin="body-small" textColor="subtle" mt={3} mb={2}>
                    {__(
                        "We'll do our best to match your project brief with your preferred designer. Otherwise, we'll pair you with another expert who'll fit your creative needs."
                    )}
                </Typography>
                <Typography fontSkin="body-small" textColor="subtle" mb={0}>
                    {__('The 24 hour turnaround time starts once your request has been accepted.')}
                </Typography>
            </Box>
            <PreferredDesignersModal
                isOpen={isModalOpen}
                onRequestDismiss={() => setIsModalOpen(false)}
            />
        </>
    );
}
